import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import StandaloneFlightStateContext from 'contexts/Flights/StandaloneFlights/standaloneFlightsStateContext'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useFlightFareFamilies from 'hooks/useFlightFareFamilies'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { arrayToMap, last, sortBy } from 'lib/array/arrayUtils'
import FareFamilyFareOption from './FareFamilyFareOption'
import FareFamilySelectFareSkeleton from './FareFamilyFareOptionSkeleton'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import HelpPane from 'components/Common/HelpPane/HelpPane'
import FlightFareRulesModalContent from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModalContent'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import FareFamilySelectModalReturnFareHeaderItem from './FareFamilySelectModalReturnFareHeaderItem'
import { FlightSegment, FlightViewTypes } from 'constants/flight'
import { getOverrideCost, getOverrideLuxPlusMemberCost, getOverrideLuxPlusRegularCost } from 'lib/flights/fareFamilyOverrideCost'
import FareFamilySelectModalSingleRouteFareHeaderItem from './FareFamilySelectModalSingleRouteFareHeaderIte'
import { getFeaturesByFareFamily, isFlightJourneyDomestic, isPerAdultPriceDisplay } from 'lib/flights/flightUtils'
import CFMRUnforeseenEventsModalContent from './CFMRUnforeseenEventsModalContent'
import { restoreIdsOfInsuranceFareFamily } from 'lib/insurance/insuranceFlights'
import useModalCloseOnLocationChange from 'hooks/Modal/useModalCloseOnLocationChange'
import FareFamilyModalDisclaimer, { FareFamilyDisclaimerContainer } from './FareFamilyModalDisclaimer'
import { useIntl } from 'react-intl'
import getFareFamilyFeederBudgetLabels, { FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE, getFareFamilyFeederRegularBudgetLabels } from 'lib/flights/fareFamilyFeederBudgetLabels'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getHotelTotalPrice } from 'checkout/selectors/view/accommodation'
import { getCharterFlightItemPrice, getFeederBundleBudget } from 'checkout/selectors/view/flights'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import { trackClientEvent } from 'analytics/analytics'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { isMaldivesCharterJourney } from 'lib/maldivesCharter/isMaldivesCharterJourney'
import MaldivesCharterRewardSeatsToggle from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterRewardSeatsToggle'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import InsufficientLoyaltyPointsModal from 'loyalty/components/InsufficientLoyaltyPointsModal'
import requestLoyaltyAccount from 'api/loyalty/requestLoyaltyAccount'
import { API_CALL } from 'actions/actionConstants'
import { FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS } from 'actions/apiActionConstants'

const ErrorPane = styled(Pane)`
  padding: ${rem(24)};
`

function getFareFamilyRegularCost(fareFamily: App.FlightFareFamily): App.FlightFareFamily['cost'] {
  return fareFamily.regularCost ?? fareFamily.cost
}

interface Props {
  searchId: string
  sessionId?: string
  occupancies: App.Occupants
  journey: App.JourneyV2
  departingJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  relativePrice?: number
  relativePriceUnit?: 'adult' | 'traveller'
  isStandalone?: boolean
  isMultiCity?: boolean
  /**
   * The caller is choosing a fare for `journey` on its own, one leg at a time, and will ask
   * again for the other leg. Set it when the fare-picking flow is per leg, rather than leaving
   * the mode to be inferred from the provider journey's `fareType`.
   *
   * A journey that came back from a return search carries `fareType: 'Return'` even though its
   * `flightGroup` holds a single leg, and that alone used to put this modal into combined-return
   * mode: it pairs every fare through `pairedUpsellIds` and renders only what it can pair. A
   * single-leg upsell response has nothing to pair with - `pairedUpsellIds` is empty on every
   * fare - so the carousel came out empty behind a successful request, with no error to show for
   * it. TripPlanner hits exactly that after a page reload re-runs an expired return search.
   */
  isSingleLegSelection?: boolean
  isLastSelection?: boolean
  canAddToUserCart?: boolean
  flightItem?: App.Checkout.FlightItem
  /**
   * Cheapest available fare for the not-yet-selected returning leg of a two-one-ways
   * bundle, applied only on the departing tab (regular, non-charter feeder flow -
   * charter feeder already has its own `feederBudgetLabelByFareFamilyId`).
   */
  cheapestReturningPrice?: number
  /*
   * Pre-fetched fare families to be sent in the useFlightFareFamilies hook
   * to avoid fetching them again from the server
   * Used for date change scenarios where we already have the upsell data
   * fetched from the server in the change date search response
   */
  cachedFareFamilies?: App.FlightsState['fareFamiliesByFlight'][0]
}

function FareFamilySelectModal({
  journey,
  searchId,
  sessionId,
  occupancies,
  departingFareFamily,
  departingJourney,
  relativePrice,
  relativePriceUnit,
  isStandalone,
  isMultiCity,
  isSingleLegSelection,
  isLastSelection,
  canAddToUserCart,
  flightItem,
  cheapestReturningPrice,
  cachedFareFamilies,
}: Props) {
  const intl = useIntl()
  const bundledHotelPrice = useAppSelector(getHotelTotalPrice)
  const charterPrice = useAppSelector(getCharterFlightItemPrice)
  const { isFeederBundle } = useAppSelector(getFeederBundleBudget)
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()
  const rewardSeatsEnabled = useRewardSeatsEnabled()
  const isDesktopUp = useScreenSizeUp('desktop')
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const loyaltyPointsBalance = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const [rewardSeatsOnly, setRewardSeatsOnly] = useState(false)
  const [fareRules, setFareRules] = useState<string | undefined>()
  const [showCfmrUnforeseenEvents, setShowCfmrUnforeseenEvents] = useState(false)
  const [cfmrUnforeseenEventsCost, setCfmrUnforeseenEventsCost] = useState<number | undefined>()
  const modal = useModalElementContext<{ fareFamily: App.FlightFareFamily, returningFareFamily?: App.FlightFareFamily, addToUserCart?: boolean, points?: App.LoyaltyPointsEarnCalculation, fareFamiliesKey?: string, isClassicReward?: boolean, rewardPointsTotal?: number } | undefined>()
  const relativePriceTravellerCount = occupancies.adults + (occupancies.children ?? 0) + (occupancies.infants ?? 0)
  const { isMixedTravellerSearch } = useContext(StandaloneFlightStateContext)
  const isPerAdultDisplay = isPerAdultPriceDisplay(Boolean(isStandalone), isMixedTravellerSearch)

  useModalCloseOnLocationChange()

  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = last(journey.flightGroup.flights)
  const isReturnType = !isSingleLegSelection && journey.fareType === FlightViewTypes.RETURN
  const isCombinedUpsell = false
  const insuranceDestinationCountry = isReturnType ? last(departingJourney?.flightGroup.flights ?? [])?.arrivalCountry : lastFlight.arrivalCountry
  const insuranceTripStartDate = isReturnType ? departingJourney?.flightGroup.flights[0].departingDate : firstFlight.departingDate
  const insuranceTripEndDate = lastFlight.arrivalDate

  // Use pre-fetched fare families if provided, otherwise fetch via hook
  const { families: fareFamilies, fetching, error, fareFamiliesKey, insuranceQuotes } = useFlightFareFamilies({
    searchId,
    sessionId: sessionId ?? '',
    occupancies,
    provider: journey.provider,
    originAirportCode: firstFlight.departureAirport,
    destinationAirportCode: lastFlight.arrivalAirport,
    destinationCountry: insuranceDestinationCountry,
    departingDate: insuranceTripStartDate,
    arrivalDate: insuranceTripEndDate, // Will be the last flight's arrival date for both One-way and Return Fares
    journeyId: journey.id,
    departingJourneyId: departingJourney?.id,
    isStandalone,
    isReturnType,
    isCombinedUpsell,
    departingFareFamily,
    flightItem,
    // Skip fetching if we have pre-fetched data
    cachedFareFamilies,
  })

  const featuresByFareFamily = useMemo(() => getFeaturesByFareFamily(fareFamilies), [fareFamilies])

  const fareFamiliesById = useMemo(() => {
    return arrayToMap(
      fareFamilies,
      family => family.id,
      family => family,
    )
  }, [fareFamilies])

  /**
   * The progressive (single-tile) two-one-ways flow: this is the *returning* leg's fare
   * selection, with the departing leg already committed (`departingFareFamily`). Unlike a true
   * RETURN fareType journey (paired via `pairedUpsellIds` below) or the combined-tile modal
   * (which always knows both legs), this modal would otherwise only show the returning leg's
   * own fare cost. Multi-city itineraries don't have upsells, so they're excluded.
   */
  const isProgressiveReturningLeg = !isReturnType && !isMultiCity && !!departingFareFamily

  const getOverrideTotalCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideCost({
      fareFamily,
      costKey: 'all',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const getOverrideAdultCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideCost({
      fareFamily,
      costKey: 'adult',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const getOverrideLuxPlusMemberTotalCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideLuxPlusMemberCost({
      fareFamily,
      costKey: 'all',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const getOverrideLuxPlusMemberAdultCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideLuxPlusMemberCost({
      fareFamily,
      costKey: 'adult',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const getOverrideLuxPlusRegularTotalCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideLuxPlusRegularCost({
      fareFamily,
      costKey: 'all',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const getOverrideLuxPlusRegularAdultCost = useCallback((fareFamily: App.FlightFareFamily) => {
    if (!isProgressiveReturningLeg) return undefined
    return getOverrideLuxPlusRegularCost({
      fareFamily,
      costKey: 'adult',
      segment: FlightSegment.RETURNING,
      selectedDepartingFareFamily: departingFareFamily,
      pairedReturnFareFamilies: [],
    })
  }, [isProgressiveReturningLeg, departingFareFamily])

  const sortedFamilies = useMemo(() => {
    if (!isReturnType) {
      return sortBy(
        fareFamilies,
        fare => fare.costWithInsurance?.all ?? fare.cost.all,
        'asc',
      )
    }

    const result: Array<App.FlightFareFamily> = []
    for (const family of fareFamilies) {
      if (!family.pairedUpsellIds) {
        continue
      }

      for (const pairedId of family.pairedUpsellIds) {
        const pairedFamily = fareFamiliesById.get(pairedId)
        if (!pairedFamily) {
          continue
        }

        /**
         * If the paired family is already in the result, we don't need to add it again
         */
        if (result.find(fare => fare.id === pairedFamily.id && !family.withEmbeddedInsurance)) {
          continue
        }

        result.push({
          ...family,
          cost: {
            all: (family.cost.all) + (pairedFamily.cost.all ?? 0),
            adult: family.cost.adult + (pairedFamily.cost.adult ?? 0),
            children: family.cost.children + (pairedFamily.cost.children ?? 0),
            infant: family.cost.infant + (pairedFamily.cost.infant ?? 0),
          },
          regularCost: {
            all: getFareFamilyRegularCost(family).all + getFareFamilyRegularCost(pairedFamily).all,
            adult: getFareFamilyRegularCost(family).adult + getFareFamilyRegularCost(pairedFamily).adult,
            children: getFareFamilyRegularCost(family).children + getFareFamilyRegularCost(pairedFamily).children,
            infant: getFareFamilyRegularCost(family).infant + getFareFamilyRegularCost(pairedFamily).infant,
          },
          ...(
            family.withEmbeddedInsurance ? {
              costWithInsurance: {
                all: (family.costWithInsurance?.all ?? 0) + (pairedFamily.costWithInsurance?.all ?? 0),
                adult: (family.costWithInsurance?.adult ?? 0) + (pairedFamily.costWithInsurance?.adult ?? 0),
                children: (family.costWithInsurance?.children ?? 0) + (pairedFamily.costWithInsurance?.children ?? 0),
                infant: (family.costWithInsurance?.infant ?? 0) + (pairedFamily.costWithInsurance?.infant ?? 0),
              },
              luxPlusCostWithInsurance: {
                all: (family.luxPlusCostWithInsurance?.all ?? family.costWithInsurance?.all ?? 0) + (pairedFamily.luxPlusCostWithInsurance?.all ?? pairedFamily.costWithInsurance?.all ?? 0),
                adult: (family.luxPlusCostWithInsurance?.adult ?? family.costWithInsurance?.adult ?? 0) + (pairedFamily.luxPlusCostWithInsurance?.adult ?? pairedFamily.costWithInsurance?.adult ?? 0),
                children: (family.luxPlusCostWithInsurance?.children ?? family.costWithInsurance?.children ?? 0) + (pairedFamily.luxPlusCostWithInsurance?.children ?? pairedFamily.costWithInsurance?.children ?? 0),
                infant: (family.luxPlusCostWithInsurance?.infant ?? family.costWithInsurance?.infant ?? 0) + (pairedFamily.luxPlusCostWithInsurance?.infant ?? pairedFamily.costWithInsurance?.infant ?? 0),
              },
            } : {}
          ),
        })
      }
    }
    return sortBy(
      result,
      fare => fare.costWithInsurance?.all ?? fare.cost.all,
      'asc',
    )
  }, [fareFamilies, fareFamiliesById, isReturnType])

  const showClassicRewardOptions = rewardSeatsEnabled && isMaldivesCharterJourney(journey) && !!isStandalone

  const sortedSelectableFamilies = useMemo(() => {
    if (!showClassicRewardOptions || !rewardSeatsOnly) return sortedFamilies

    return sortedFamilies.filter(fareFamily => !fareFamily.withEmbeddedInsurance)
  }, [rewardSeatsOnly, showClassicRewardOptions, sortedFamilies])

  /**
   * Two different dead ends, told apart because they need different things said to the traveller.
   *
   * `loadFailed` is the upsell request itself failing - retrying or calling support can genuinely
   * help. `hasNoFaresToOffer` is a request that succeeded and returned nothing this modal can put
   * on screen, which is what an expired or re-run search produces; "something went wrong" would
   * be a lie there, and retrying the same modal would land in the same place. Neither may render
   * as a lone disclaimer, which is what an empty carousel used to look like.
   */
  const loadFailed = !!error
  const hasNoFaresToOffer = !loadFailed && !fetching && sortedFamilies.length === 0
  const showErrorState = loadFailed || hasNoFaresToOffer

  const errorStateCopy = useMemo(() => {
    if (loadFailed) {
      return {
        heading: intl.formatMessage({
          id: 'DDY5BMNxXtZUlN2Tgji3',
          defaultMessage: 'Oh no, something went wrong',
          description: 'Error state heading when fare families fail to load',
        }),
        body: intl.formatMessage({
          id: 'OT1h6sZVbDe-jeqPMvU9',
          defaultMessage: 'We ran into a problem when trying to load the information. Please go back and try again or call our support team on the number below.',
          description: 'Error notification displayed when fare families fail to load',
        }),
      }
    }

    return {
      heading: intl.formatMessage({
        id: '1VdY9lM7hDgrZodzdJDV',
        defaultMessage: "We couldn't show fares for this flight",
        description: 'Heading shown inside the flight fare selection modal when the fare upsell request succeeded but returned no fare options that can be offered for this flight',
      }),
      body: intl.formatMessage({
        id: '-KK79uT9mw1bdcoePCPA',
        defaultMessage: 'There are no fare options we can show for this flight right now - its fares may have changed since you searched. Please close this and search again, or call our support team on the number below.',
        description: 'Body copy shown inside the flight fare selection modal when no fare options can be offered, explaining that the fares may have changed since the search and giving the traveller next steps',
      }),
    }
  }, [intl, loadFailed])

  /**
   * A successful request that yields nothing selectable is invisible to every other signal we
   * have: no failed call, no thrown error, nothing in the logs. Without this it can only ever
   * reach us as a customer report, which is how the combined-return regression stayed live.
   */
  useEffect(() => {
    if (!hasNoFaresToOffer) return

    trackClientEvent({
      subject: 'fare_family_select_modal',
      action: 'no_fares_available',
      category: 'flight_search',
      type: 'operational',
    })
  }, [hasNoFaresToOffer])

  const modalRef = useRef<HTMLDivElement>(null)
  const onHideFareRules = useCallback(() => {
    setFareRules(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onShowFareRules = useCallback((url: string) => {
    setFareRules(url)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onHideCfmrUnforeseenEvents = useCallback(() => {
    setShowCfmrUnforeseenEvents(false)
    setCfmrUnforeseenEventsCost(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const fareRuleTitle = intl.formatMessage({
    id: '3vt1eJJoNN4iANV6CUn6',
    defaultMessage: 'Fare rule',
    description: 'Modal title when viewing fare rules in the flights section',
  })
  const refundableTitle = intl.formatMessage({
    id: 'g0FS7g6K21mGdbERnc-X',
    defaultMessage: 'Make your flight fully refundable',
    description: 'Modal title for flight refund insurance option on the form',
  })
  const selectFareTitle = intl.formatMessage({
    id: 'G0WGd3zx0N0QHD9VdUPi',
    defaultMessage: 'Select a fare',
    description: 'Modal title for fare family selection in the flights section',
  })
  const cfmrSubtitle = intl.formatMessage({
    id: 'cfM574oiyll_VZO5grea',
    defaultMessage: 'This exclusive protection from Luxury Escapes and XCover ensures a hassle-free 100% refund if you need to cancel for unforeseen events.',
    description: 'Subtitle explaining the cancellation protection coverage from Luxury Escapes and XCover',
  })
  const title = fareRules ? fareRuleTitle : showCfmrUnforeseenEvents ? refundableTitle : selectFareTitle

  const onSelectFareFamily = useCallback(({ fareFamily, points, isClassicReward, rewardPointsTotal }: {
    fareFamily: App.FlightFareFamily
    points?: App.LoyaltyPointsEarnCalculation
    isClassicReward?: boolean
    rewardPointsTotal?: number
  }) => {
    // The selected fare family contains modified cost values for Return Fares.
    // We need to get the original fare family from the fareFamiliesById map.
    const originalFareFamily = fareFamiliesById.get(fareFamily.id)
    if (!originalFareFamily) return

    let returningFareFamily = fareFamily.pairedUpsellIds ? fareFamiliesById.get(fareFamily.pairedUpsellIds[0]) : undefined

    if (returningFareFamily && originalFareFamily.withEmbeddedInsurance) {
      returningFareFamily = {
        ...returningFareFamily,
        withEmbeddedInsurance: true,
      }
    }

    // We need to restore the original IDs since the '-insurance' suffix was only used
    // to differentiate between the original fare and the insurance-included version in the UI.
    // The booking flow requires the original IDs as they map to the actual fare orders.
    const sanitizedReturningFareFamily = returningFareFamily ? restoreIdsOfInsuranceFareFamily(returningFareFamily) : undefined
    const sanitizedOriginalFareFamily = restoreIdsOfInsuranceFareFamily(originalFareFamily)

    if (isClassicReward) {
      dispatch({ type: API_CALL, api: FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS, request: () => requestLoyaltyAccount() })
    }

    if (isClassicReward && rewardPointsTotal !== undefined && rewardPointsTotal > loyaltyPointsBalance) {
      showModal(<InsufficientLoyaltyPointsModal rewardPointsDeficit={rewardPointsTotal - loyaltyPointsBalance} />)
      return
    }

    modal.resolve({
      fareFamily: sanitizedOriginalFareFamily,
      returningFareFamily: sanitizedReturningFareFamily,
      addToUserCart: isWeddingGiftRegistry && isLastSelection && canAddToUserCart ? true : undefined,
      points,
      fareFamiliesKey,
      isClassicReward,
      rewardPointsTotal,
    })
  }, [canAddToUserCart, fareFamiliesById, fareFamiliesKey, isLastSelection, isWeddingGiftRegistry, modal, loyaltyPointsBalance, showModal, dispatch])

  const isDomestic = useMemo(() => {
    return isFlightJourneyDomestic(journey.flightGroup.flights)
  }, [journey])

  const feederBudgetLabelByFareFamilyId = useMemo(() => getFareFamilyFeederBudgetLabels({
    mode: FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE,
    flightItem,
    isFeederBundle,
    fareFamilies: sortedFamilies,
    isReturnType,
    departingJourney,
    baseline: bundledHotelPrice + charterPrice,
  }), [flightItem, isFeederBundle, sortedFamilies, isReturnType, departingJourney, bundledHotelPrice, charterPrice])

  // Non-member reference-price counterpart: same baseline/budget/charter rules, run on each
  // fare family's regular (undiscounted) cost, so the LuxPlus "Non-member $X" comparison price
  // for a feeder-bundle option never drifts from the real member total above.
  const feederRegularBudgetLabelByFareFamilyId = useMemo(() => getFareFamilyFeederRegularBudgetLabels({
    mode: FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE,
    flightItem,
    isFeederBundle,
    fareFamilies: sortedFamilies,
    isReturnType,
    departingJourney,
    baseline: bundledHotelPrice + charterPrice,
  }), [flightItem, isFeederBundle, sortedFamilies, isReturnType, departingJourney, bundledHotelPrice, charterPrice])

  const onCloseButtonClick = useCallback(() => {
    if (fareRules) return onHideFareRules()
    if (showCfmrUnforeseenEvents) return onHideCfmrUnforeseenEvents()
    return modal.resolve(undefined)
  }, [fareRules, modal, onHideCfmrUnforeseenEvents, onHideFareRules, showCfmrUnforeseenEvents])

  const modalSize = useMemo(() => {
    if (fareRules) return 'L'
    if (showCfmrUnforeseenEvents) return 'S'
    return 'auto'
  }, [fareRules, showCfmrUnforeseenEvents])

  const rewardSeatsFilterPill = showClassicRewardOptions ? (
    <MaldivesCharterRewardSeatsToggle
      checked={rewardSeatsOnly}
      onChange={setRewardSeatsOnly}
    />
  ) : null

  const modalTitle = !fareRules && !showCfmrUnforeseenEvents && rewardSeatsFilterPill && isDesktopUp ?
      <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
        <span style={{ minWidth: 0 }}>{title}</span>
        {rewardSeatsFilterPill}
      </Group> :
    title

  return (<ModalBase size={modalSize} data-testid="fare-family-select-modal">
    <ModalHeader
      title={modalTitle}
      subtitle={showCfmrUnforeseenEvents ? cfmrSubtitle : undefined}
      onCloseButtonClick={onCloseButtonClick}
      onBackButtonClick={fareRules ? onCloseButtonClick : undefined}
    >
      {!fareRules && !showCfmrUnforeseenEvents && rewardSeatsFilterPill && !isDesktopUp ? rewardSeatsFilterPill : undefined}
    </ModalHeader>
    <ModalBody ref={modalRef}>
      <ModalContent>
        <div data-fare-family-type={isReturnType ? 'return' : 'one-way'}>
          {!fareRules && !showCfmrUnforeseenEvents && (
            <VerticalSpacer gap={24}>
              <VerticalSpacer gap={4}>
                {!isReturnType && <FareFamilySelectModalSingleRouteFareHeaderItem flight={journey} />}

                {isReturnType && departingJourney && (
                  <FareFamilySelectModalReturnFareHeaderItem flights={[departingJourney, journey]} />
                )}
              </VerticalSpacer>

              {showErrorState && <ErrorPane background="light-background" data-testid={hasNoFaresToOffer ? 'fare-family-no-fares' : 'fare-family-load-error'}>
                <VerticalSpacer gap={16}>
                  <Heading variant="heading5">{errorStateCopy.heading}</Heading>
                  <BodyText variant="large">{errorStateCopy.body}</BodyText>
                  <HelpPane />
                </VerticalSpacer>
              </ErrorPane> }

              {!showErrorState && <VerticalSpacer gap={12}>
                <CardCarousel
                  width={rem(275)}
                  gap={16}
                  gutterStyle="overflow"
                  gutterSize={24}
                >
                  {fetching && <>
                    <FareFamilySelectFareSkeleton />
                    <FareFamilySelectFareSkeleton />
                    <FareFamilySelectFareSkeleton />
                  </>}
                  {sortedSelectableFamilies.map((fareFamily) => <FareFamilyFareOption
                    selected={false}
                    key={fareFamily.id}
                    fareFamily={fareFamily}
                    features={featuresByFareFamily.get(fareFamily.id)}
                    onSelect={onSelectFareFamily}
                    onShowFareRules={onShowFareRules}
                    relativePrice={relativePrice}
                    relativePriceUnit={relativePriceUnit}
                    relativePriceTravellerCount={relativePriceTravellerCount}
                    isReturnType={isReturnType}
                    journey={journey}
                    departingFareFamily={departingFareFamily}
                    rewardDepartingFareFamily={fareFamiliesById.get(fareFamily.id)}
                    returningFareFamily={fareFamily.pairedUpsellIds?.[0] ? fareFamiliesById.get(fareFamily.pairedUpsellIds[0]) : undefined}
                    occupancies={occupancies}
                    isPerAdultPrice={isPerAdultDisplay}
                    isBundledFlow={!isStandalone}
                    isDomestic={isDomestic}
                    isMultiCity={isMultiCity}
                    bundledHotelPrice={bundledHotelPrice}
                    cheapestReturningPrice={!isFeederBundle && !isReturnType && !isProgressiveReturningLeg ? cheapestReturningPrice : undefined}
                    feederBudgetLabel={feederBudgetLabelByFareFamilyId?.get(fareFamily.id)}
                    feederRegularBudgetLabel={feederRegularBudgetLabelByFareFamilyId?.get(fareFamily.id)}
                    overrideTotalCost={getOverrideTotalCost(fareFamily)}
                    overrideAdultCost={getOverrideAdultCost(fareFamily)}
                    luxPlusMemberCost={getOverrideLuxPlusMemberTotalCost(fareFamily)}
                    luxPlusMemberAdultCost={getOverrideLuxPlusMemberAdultCost(fareFamily)}
                    luxPlusRegularCost={getOverrideLuxPlusRegularTotalCost(fareFamily)}
                    luxPlusRegularAdultCost={getOverrideLuxPlusRegularAdultCost(fareFamily)}
                    showClassicRewardOption={showClassicRewardOptions && !fareFamily.withEmbeddedInsurance}
                    showCashOption={!rewardSeatsOnly}
                  />)}
                </CardCarousel>

                <FareFamilyDisclaimerContainer>
                  <FareFamilyModalDisclaimer />
                </FareFamilyDisclaimerContainer>
              </VerticalSpacer>}
            </VerticalSpacer>
          )}

          {fareRules && (
            <FlightFareRulesModalContent
              urls={[fareRules]}
            />
          )}

          {insuranceTripStartDate && showCfmrUnforeseenEvents && (
            <CFMRUnforeseenEventsModalContent
              occupants={occupancies}
              destinationCountry={insuranceDestinationCountry}
              departingDate={insuranceTripStartDate}
              arrivalDate={insuranceTripEndDate}
              cfmrUnforeseenEventsCost={cfmrUnforeseenEventsCost}
              insuranceQuotes={insuranceQuotes}
            />
          )}
        </div>
      </ModalContent>
    </ModalBody>
  </ModalBase>)
}

export default FareFamilySelectModal
